/** Sends an old librarian URL to its new route, query parameters included, and toasts a notice. */

import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Info, ArrowRight, Clock, CheckCircle } from 'lucide-react';
import { 
  trackMigration, 
  generateMigrationMessage, 
  buildMigratedUrl, 
  findNewRoute 
} from '@/utils/route-migration';

interface RouteRedirectProps {
  from: string;
  to: string;
  tabParam?: string;
  preserveParams?: boolean;
  showDeprecationNotice?: boolean;
  deprecationMessage?: string;
  delayMs?: number; // Optional delay for user to see the notice
}

const RouteRedirect: React.FC<RouteRedirectProps> = ({
  from,
  to,
  tabParam,
  preserveParams = true,
  showDeprecationNotice = true,
  deprecationMessage,
  delayMs = 0
}) => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [shouldRedirect, setShouldRedirect] = useState(delayMs === 0);

  // Build the new URL with preserved parameters
  const buildRedirectUrl = (): string => {
    // Try to use the migration utilities first
    const mapping = findNewRoute(from);
    if (mapping && preserveParams) {
      return buildMigratedUrl(mapping, searchParams);
    }
    
    // Fallback to manual parameter handling
    const newParams = new URLSearchParams();
    
    // Add the tab parameter if specified
    if (tabParam) {
      newParams.set('tab', tabParam);
    }
    
    // Preserve existing parameters if requested
    if (preserveParams) {
      searchParams.forEach((value, key) => {
        // Don't override the tab parameter if it was explicitly set
        if (key !== 'tab' || !tabParam) {
          newParams.set(key, value);
        }
        
        // Handle special parameter mappings for backward compatibility
        if (key === 'bookId' && tabParam === 'issue-return') {
          newParams.set('selectedBook', value);
        }
        if (key === 'memberId' && tabParam === 'issue-return') {
          newParams.set('selectedMember', value);
        }
        if (key === 'action' && (value === 'issue' || value === 'return')) {
          newParams.set('workflow', value);
        }
      });
    }
    
    const queryString = newParams.toString();
    return queryString ? `${to}?${queryString}` : to;
  };

  // Handle delayed redirect
  useEffect(() => {
    if (delayMs > 0) {
      const timer = setTimeout(() => {
        setShouldRedirect(true);
      }, delayMs);
      
      return () => clearTimeout(timer);
    }
  }, [delayMs]);

  // Show deprecation notice and track migration
  useEffect(() => {
    const redirectUrl = buildRedirectUrl();
    
    // Track the migration
    trackMigration(from, redirectUrl);
    
    if (showDeprecationNotice) {
      const message = deprecationMessage || generateMigrationMessage(from, redirectUrl);
      
      toast.info('This page has moved', {
        description: message,
        duration: 5000,
      });
    }
  }, [from, to, showDeprecationNotice, deprecationMessage]);

  const redirectUrl = buildRedirectUrl();

  // Show loading state if there's a delay
  if (delayMs > 0 && !shouldRedirect) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 p-3 bg-blue-100 dark:bg-blue-900/20 rounded-full w-fit">
              <ArrowRight className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            </div>
            <CardTitle className="text-xl">Redirecting...</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription>
                {deprecationMessage || `Redirecting from ${from} to the new consolidated interface.`}
              </AlertDescription>
            </Alert>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">From:</span>
                <Badge variant="outline" className="font-mono text-xs">
                  {from}
                </Badge>
              </div>
              <div className="flex items-center justify-center">
                <ArrowRight className="h-3 w-3 text-gray-400" />
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">To:</span>
                <Badge variant="default" className="font-mono text-xs">
                  {to}{tabParam ? `?tab=${tabParam}` : ''}
                </Badge>
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
              <Clock className="h-3 w-3 animate-pulse" />
              <span>Redirecting in a moment...</span>
            </div>
            
            <Button 
              onClick={() => setShouldRedirect(true)}
              className="w-full"
              size="sm"
            >
              Continue Now
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return shouldRedirect ? <Navigate to={redirectUrl} replace /> : null;
};

/**
 * Deprecation Notice Component
 * A notice about the moved route, shown before continuing
 */
interface DeprecationNoticeProps {
  oldRoute: string;
  newRoute: string;
  onContinue: () => void;
}

export const DeprecationNotice: React.FC<DeprecationNoticeProps> = ({
  oldRoute,
  newRoute,
  onContinue
}) => {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50 dark:bg-gray-900">
      <Card className="max-w-md w-full">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5 text-blue-500" />
            This page has moved
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              The URL <code className="bg-gray-100 dark:bg-gray-800 px-1 rounded">{oldRoute}</code> 
              has moved.
            </AlertDescription>
          </Alert>
          
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <span>Old:</span>
            <code className="bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded text-xs">
              {oldRoute}
            </code>
          </div>
          
          <div className="flex items-center gap-2">
            <ArrowRight className="h-4 w-4 text-gray-400" />
          </div>
          
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <span>New:</span>
            <code className="bg-green-100 dark:bg-green-900 px-2 py-1 rounded text-xs">
              {newRoute}
            </code>
          </div>
          
          <div className="flex flex-col gap-2 pt-4">
            <Button onClick={onContinue} className="w-full">
              Go to the new page
            </Button>
          </div>
          
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Update your bookmark. This redirect will be removed later.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default RouteRedirect;